import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Loader, Table } from 'semantic-ui-react';
import { gridsMenuSelector, roleIdSelector, roleSelector } from '../../ducks/profile';
import { getLookupRequest, valuesListSelector, companyListSelector } from '../../ducks/lookup';
import './style.scss';
import InfiniteScrollTable from '../../components/InfiniteScrollTable';
import fieldsSetting, {
    clearFieldsSettings,
    editProgressSelector,
    progressSelector,
    scenarioSettingSelector,
    getScenarioSettingRequest,
    editScenarioSettingRequest,
    scenarioSettingCompaniesSelector,
    getScenarioSettingCompaniesRequest
} from '../../ducks/fieldsSetting';
import { ORDERS_GRID } from '../../constants/grids';
import Header from './components/header';
import ScenarioTableBody from './components/scenario_table_body';
import ScenarioTableHeader from './components/scenario_table_header';
import { sortFunc } from '../../utils/sort';

const ScenarioList = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const containerRef = useRef(null);

    let [role, setRole] = useState(null);
    let [company, setCompany] = useState(null);

    const gridsList = useSelector(state => gridsMenuSelector(state)) || [];
    const rolesList = useSelector(state => companyListSelector(state, 'roles', company)) || [];
    const companiesList = useSelector(state => scenarioSettingCompaniesSelector(state)) || [];
    const scenarioSettings = useSelector(state => scenarioSettingSelector(state)) || [];
    const loading = useSelector(state => progressSelector(state));
    const editProgress = useSelector(state => editProgressSelector(state)) || false;
    const userRole = useSelector(state => roleIdSelector(state));

    let [activeItem, setActiveItem] = useState((gridsList[0] || {}).name);

    const statusList =
        useSelector(state =>
            valuesListSelector(state, `${activeItem.slice(0, activeItem.length - 1)}State`),
        ) || [];

    useEffect(() => {
        dispatch(
            getLookupRequest({
                name: 'roles',
                isForm: true,
            }),
        );

        dispatch(
            getScenarioSettingCompaniesRequest(),
        );
    }, [userRole]);

    useEffect(() => {
        activeItem && getStatus();
    }, [activeItem]);

    useEffect(() => {
        if (rolesList.length) {
            setRole(rolesList[0].value);
        } 
    }, [company]);

    useEffect(() => {
        if (!role && rolesList.length) {
            setRole(userRole);
        } 
    }, [rolesList]);

    useEffect(() => {
        if (!company && companiesList.length) {
            setCompany(companiesList[0].value);
        }
    }, [companiesList]);

    useEffect(() => {
        dispatch(clearFieldsSettings());
        activeItem && company && getSettings();
    }, [activeItem, company]);

    const getSettings = () => {
        dispatch(
            getScenarioSettingRequest({
                forEntity: activeItem,
                companyId: company,
            }),
        );
    };

    const getStatus = () => {
        dispatch(
            getLookupRequest({
                name: `${activeItem.slice(0, activeItem.length - 1)}State`,
                isForm: true,
                isState: true,
                params: {},
            }),
        );
    };

    const handleChangeActiveItem = useCallback((e, { value }) => {
        setActiveItem(value);
    }, []);

    const handleChangSettings = useCallback(
        (fieldName, accessType, state = null, extType) => {
            dispatch(
                editScenarioSettingRequest({
                    params: {
                        forEntity: activeItem,
                        companyId: company === null ? undefined : company,
                        scenario: fieldName,
                        isAllowed: accessType,
                        state
                    },
                    extType,
                    callbackSuccess: () => {
                        getSettings();
                    }
                })
            )
        },
        [activeItem, role, company],
    );


    const handleChangeRole = useCallback((e, { value }) => {
        setRole(value);
    }, []);

    const handleChangeCompany = useCallback((e, { value }) => {
        setCompany(value);
    }, []);

    const { base: baseSettings = [], ext: extSettings = [] } = scenarioSettings;

    return (
        <div className="container">
            <Loader active={loading && !baseSettings.length} size="huge" className="table-loader">
                Loading
            </Loader>
            <Header
                gridsList={gridsList}
                activeItem={activeItem}
                changeActiveItem={handleChangeActiveItem}
                rolesList={rolesList}
                companiesList={companiesList}
                role={role}
                company={company}
                changeRole={handleChangeRole}
                changeCompany={handleChangeCompany}
                isScenario={true}
                t={t}
            />
            
                <div className={`scroll-table-container field-settings-table`} ref={containerRef}>
                    <InfiniteScrollTable
                        className="grid-table table-info"
                        onBottomVisible={() => {}}
                        structured
                        context={containerRef.current}
                        headerRow={<ScenarioTableHeader statusList={statusList} t={t} />}
                    >
                        <Table.Body className="table-fields-setting">
                            {baseSettings.length ? (
                                <>
                                    <Table.Row>
                                        <Table.Cell className="field-settings-table_fixed">
                                            <div className="ui ribbon label">{t('Main fields')}</div>
                                        </Table.Cell>
                                        {statusList.map((state, i) => (
                                            <Table.Cell key={i} />
                                        ))}
                                    </Table.Row>
                                    {sortFunc(baseSettings, t, 'displayName').map(column => (
                                        <ScenarioTableBody
                                            key={column.scenario}
                                            column={column}
                                            statusList={statusList}
                                            editProgress={editProgress}
                                            changeSettings={handleChangSettings}
                                            t={t}
                                        />
                                    ))}
                                </>
                        ) : null}

                        {Object.entries(extSettings).map(([extType, extValues]) => (
                            <>
                                <Table.Row>
                                    <Table.Cell className="field-settings-table_fixed">
                                        <div className="ui ribbon label">
                                            {t(extType)}
                                        </div>
                                    </Table.Cell>
                                    {statusList.map((state, i) => (
                                        <Table.Cell key={i} />
                                    ))}
                                </Table.Row>
                                {sortFunc(extValues, t, 'displayName').map(column => (
                                    <ScenarioTableBody
                                        key={column.scenario}
                                        column={column}
                                        statusList={statusList}
                                        changeSettings={handleChangSettings}
                                        editProgress={editProgress}
                                        t={t}
                                        extType={extType}
                                    />
                                ))}
                            </>
                        ))}
                        </Table.Body>
                    </InfiniteScrollTable>
                </div>
               
        </div>
    );
};

export default ScenarioList;
